'use client';
import React, { useState, useEffect, useRef } from 'react';

interface Stat {
  num: string;
  value: string;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  { num: '01 / 04', value: '6.5', suffix: 'B', label: 'Barrels Estimated Reserves' },
  { num: '02 / 04', value: '1,443', suffix: 'km', label: 'Pipeline Length (EACOP)' },
  { num: '03 / 04', value: '60K', suffix: '', label: 'Barrels/Day Target Production' },
  { num: '04 / 04', value: '10', suffix: '+', label: 'Years of Operations' },
];

export default function StatsSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 bg-[#0F0F0F] relative overflow-hidden"
    >
      {/* Dot grid background */}
      <div
        className="absolute inset-0"
        style={{
          opacity: 0.04,
          backgroundImage: 'radial-gradient(circle at 2px 2px, #CC0000 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div
            className="lg:col-span-7"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(20px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 04 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'Inter, sans-serif' }}>Key Figures</span>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}
            >
              Powering Uganda by <span className="text-[#CC0000]">the numbers</span>
            </h2>
          </div>
          <div
            className="lg:col-span-5 lg:pt-12"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(20px)',
              transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s',
            }}
          >
            <p className="text-base text-gray-400 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              Concrete milestones tracking UNOC&apos;s contribution to Uganda&apos;s energy sector — from upstream reserves to operational infrastructure built over a decade of work.
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="border-r border-b border-white/10 p-8 lg:p-10 group hover:bg-[#CC0000] transition-colors duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'translateY(20px)',
                transition: `opacity 0.6s ease ${0.1 * (i + 1)}s, transform 0.6s ease ${0.1 * (i + 1)}s, background-color 0.3s ease`,
              }}
            >
              <span className="block font-mono text-xs text-gray-500 group-hover:text-white/70 tracking-[0.2em] mb-6 transition-colors">
                {stat.num}
              </span>
              <div
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-none mb-4 tracking-tight"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {stat.value}
                {stat.suffix && (
                  <span className="text-[#CC0000] group-hover:text-white transition-colors">{stat.suffix}</span>
                )}
              </div>
              <p className="text-sm text-gray-400 group-hover:text-white leading-relaxed transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
